import { useEffect, useState } from 'react';
import { TelegramWebApp } from '../types/payment';
import { logger } from '@core/logger';

export const useTelegramWebApp = () => {
  const [webApp, setWebApp] = useState<TelegramWebApp | null>(null);
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    logger.debug('🔍 [useTelegramWebApp] Hook initialized');
    logger.debug('🔍 [useTelegramWebApp] window.Telegram:', !!window.Telegram);
    logger.debug('🔍 [useTelegramWebApp] window.Telegram?.WebApp:', !!window.Telegram?.WebApp);
    
    // Check if we're running inside Telegram WebApp
    if (window.Telegram?.WebApp) {
      const tgWebApp = window.Telegram.WebApp;

      // The Telegram SDK script may be loaded in non-Telegram contexts (WhatsApp, browser).
      // Without real initData, calling ready()/expand() makes the SDK apply its dark theme
      // colors to the DOM, causing a black screen. Only initialize if there is actual
      // Telegram session data.
      const rawInitData = typeof tgWebApp.initData === 'string' ? tgWebApp.initData.trim() : '';
      if (!rawInitData) {
        logger.debug('🔍 [useTelegramWebApp] Telegram SDK present but no initData — not inside Telegram, skipping init');
        setIsReady(true);
        return;
      }

      logger.debug('🔍 [useTelegramWebApp] Telegram WebApp detected, initializing...');

      try {
        // Initialize the WebApp
        tgWebApp.ready();
        logger.debug('🔍 [useTelegramWebApp] WebApp.ready() called');

        // Expand the WebApp to full height
        tgWebApp.expand();
        logger.debug('🔍 [useTelegramWebApp] WebApp.expand() called');

        setWebApp(tgWebApp);
        setIsReady(true);
        
        logger.debug('🔍 [useTelegramWebApp] Telegram WebApp initialized successfully:', {
          user: tgWebApp.initDataUnsafe.user,
          chat: tgWebApp.initDataUnsafe.chat,
          theme: tgWebApp.themeParams,
          colorScheme: tgWebApp.colorScheme,
          closeMethod: typeof tgWebApp.close,
          isExpanded: tgWebApp.isExpanded
        });
      } catch (error) {
        logger.error('🔍 [useTelegramWebApp] Error initializing Telegram WebApp:', error);
        // Even if there's an error, we still have the WebApp object
        setWebApp(tgWebApp);
        setIsReady(true);
      }
    } else {
      logger.debug('🔍 [useTelegramWebApp] Not running inside Telegram WebApp');
      logger.debug('🔍 [useTelegramWebApp] window object:', !!window);
      logger.debug('🔍 [useTelegramWebApp] window.Telegram object:', !!window.Telegram);
      setIsReady(true);
    }
  }, []);

  const closeWebApp = () => {
    logger.debug('🔍 [useTelegramWebApp] closeWebApp called');
    logger.debug('🔍 [useTelegramWebApp] webApp available:', !!webApp);
    logger.debug('🔍 [useTelegramWebApp] window.Telegram?.WebApp available:', !!window.Telegram?.WebApp);
    
    try {
      if (webApp && typeof webApp.close === 'function') {
        logger.debug('🔍 [useTelegramWebApp] Closing via webApp.close()');
        webApp.close();
      } else if (window.Telegram?.WebApp && typeof window.Telegram.WebApp.close === 'function') {
        logger.debug('🔍 [useTelegramWebApp] Closing via window.Telegram.WebApp.close()');
        window.Telegram.WebApp.close();
      } else {
        logger.debug('🔍 [useTelegramWebApp] No close method available');
      }
    } catch (error) {
      logger.error('🔍 [useTelegramWebApp] Error in closeWebApp:', error);
    }
  };

  const showMainButton = (text: string, callback: () => void) => {
    if (webApp) {
      webApp.MainButton.setText(text);
      webApp.MainButton.onClick(callback);
      webApp.MainButton.show();
    }
  };

  const hideMainButton = () => {
    if (webApp) {
      webApp.MainButton.hide();
    }
  };

  const showBackButton = (callback: () => void) => {
    if (webApp) {
      webApp.BackButton.onClick(callback);
      webApp.BackButton.show();
    }
  };

  const hideBackButton = () => {
    if (webApp) {
      webApp.BackButton.hide();
    }
  };

  const hapticFeedback = (type: 'light' | 'medium' | 'heavy' | 'rigid' | 'soft' = 'light') => {
    if (webApp) {
      webApp.HapticFeedback.impactOccurred(type);
    }
  };

  // Solo tratar como Telegram WebApp si hay initData real (user, query_id o auth_date).
  // Si solo existe window.Telegram.WebApp (p. ej. por cargar el script en web normal), no cerrar automático.
  const hasTelegramInitData =
    !!webApp &&
    (webApp.initDataUnsafe?.user != null ||
      (webApp.initDataUnsafe as { query_id?: string })?.query_id != null ||
      (webApp.initDataUnsafe as { auth_date?: number })?.auth_date != null);
  const isTelegramWebApp = !!webApp && hasTelegramInitData;

  /** Raw query string from Telegram for server-side HMAC validation (do not trust client-side user alone). */
  const initData =
    typeof webApp?.initData === 'string' && webApp.initData.trim() ? webApp.initData.trim() : '';

  return {
    webApp,
    isReady,
    isTelegramWebApp,
    initData,
    closeWebApp,
    showMainButton,
    hideMainButton,
    showBackButton,
    hideBackButton,
    hapticFeedback,
    user: webApp?.initDataUnsafe.user,
    chat: webApp?.initDataUnsafe.chat,
    themeParams: webApp?.themeParams,
    colorScheme: webApp?.colorScheme,
  };
}; 